import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  ButtonToolbar,
  Loader,
} from "rsuite";
import { CustomerServices } from "../../../core/services/CustomerServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { BulkCustomerFields, CustomerFields } from "./CustomerDataTypes";
import "./Customers.css";
import CustomerBulkParams from "./CustomerBulkParams";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import {
  messageTypeArr,
  appConstObj,
  roleTypeArr,
  genderObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import EditCell from "../../components/Elements/EditCell";
import CustomerBulkImportAction from "./CustomerBulkImportAction";
import CustomerFileUpload from "./CustomerFileUpload";
import SelectEditCell from "../../components/Elements/SelectEditCell";
import CustomCell from "../../components/Elements/CustomCell";

function CustomerImport() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [mobileLen, setMobileLen] = useState(10);
  const [dataToImport, setDataToImport] = useState<CustomerFields[]>([]);
  const [importedRows, setImportedRows] = useState<any[]>([]);
  const [failedRows, setFailedRows] = useState<any[]>([]);
  const [existsRowsInDb, setExistsRowsInDb] = useState<any[]>([]);

  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const company = useSelector((state: any) => state.company);
  const [showFile, setShowFile] = useState(true);
  const handleFileToggle = () => setShowFile(!showFile);
  const [showForm, setShowForm] = useState(false);
  const handleFormToggle = () => setShowForm(!showForm);

  const initFileData = {
    company_id: getCompanyId(),
    mobile_len: 10,
    country_code_from_mobile: 1,
  };

  const initFormData = {
    company_id: "",
    store_id: "",
    building_id: "",
    area_id: "",
    city_id: "",
    state_id: "",
    country_id: "",
    customer_source: "",
    customers: "",
  };

  const [formData, setFormData] = useState<BulkCustomerFields>(initFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === dataToImport?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (
    checkedKeys?.length > 0 &&
    checkedKeys?.length < dataToImport?.length
  ) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? dataToImport.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: any, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };
  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }
  async function getCustomers(custList: CustomerFields[]) {
    try {
      setDataToImport(custList);
      setTotal(custList.length);
      setCheckedKeys([]);
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function init() {
    try {
    } catch (error) {
      console.log(error);
    }
  }

  async function uploadXLSX(fileUploadParams: any) {
    try {
      setLoading(true);
      const response: any = await CustomerServices.importCustomerFile(
        fileUploadParams
      );
      setFailedRows(response.data.data.failed_customers);
      getCustomers(response.data.data.new_customers);
      setExistsRowsInDb(response.data.data.existing_customers);
      setImportedRows([]);
      setShowFile(false);
      setShowForm(true);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
    } finally {
      setLoading(false);
    }
  }

  function reduceImportData(reduceData: any) {
    let newImportData: any[] = [];
    let importedRec = false;
    dataToImport.forEach(function (item: any) {
      importedRec = false;
      for (let i = 0; i < reduceData.length; i++) {
        const rec = reduceData[i];
        if (item.mobile === rec.mobile) {
          importedRec = true;
          break;
        }
      }
      if (importedRec == false) {
        newImportData.push(item);
      }
    });
    return newImportData;
  }

  async function bulkImportCustomers(paramsToImport: any) {
    try {
      setLoading(true);
      let response = await CustomerServices.importBulkCustomers(paramsToImport);
      setFailedRows(response.data.data.invalid);
      setImportedRows(response.data.data.valid);
      getCustomers(reduceImportData(response.data.data.valid));

      setLoading(false);
      UtilityServices.showToaster({
        message: response?.data?.message,
      });
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  const handleCellChange = (idv: any, key: any, value: any) => {
    const newData: any[] = [];
    dataToImport.forEach(function (item: any) {
      if (item.id === idv) {
        item[key] = value;
      }
      newData.push(item);
    });
    setDataToImport(newData);
  };

  return (
    <div className="customers">
      {loading && (
        <Loader
          style={{ zIndex: 9999 }}
          size="lg"
          backdrop
          content="Loading..."
          vertical
        />
      )}
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4 className="mb-2">
                {t("IMPORT")} {t("CUSTOMERS")}
              </h4>
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </Header>
        <Content>
          <div id="file_part">
            <div className="row mb-2 mb-hide">
              <ButtonToolbar>
                <Button onClick={handleFileToggle}>
                  {t("STEP1")}: {t("UPLOAD")} {t("FILE")}
                </Button>
              </ButtonToolbar>
            </div>
            {showFile && (
              <div>
                <CustomerFileUpload
                  onFileImport={uploadXLSX}
                  onSetMobileLen={setMobileLen}
                />
              </div>
            )}
          </div>
          <div id="form_part">
            <div className="row mb-2 mb-hide">
              <ButtonToolbar>
                <Button onClick={handleFormToggle}>
                  {t("STEP2")}: {t("IMPORT")} {t("UPLOADED")} {t("DATA")}
                </Button>
              </ButtonToolbar>
            </div>
            {showForm && (
              <div>
                <CustomerBulkParams
                  checkedKeys={checkedKeys}
                  setCheckedKeys={setCheckedKeys}
                  onBulkImport={bulkImportCustomers}
                  data={dataToImport}
                  mobileLength={mobileLen}
                />
                <CustomerBulkImportAction
                  importedData={importedRows}
                  failedData={failedRows}
                  existingData={existsRowsInDb}
                />

                <div>
                  <Table
                    height={400}
                    fillHeight={true}
                    data={dataToImport}
                    loading={loading}
                    bordered={true}
                    cellBordered
                    hover={true}
                  >
                    <Column width={50} align="center" fixed>
                      <CustomHeaderCell>
                        <div>
                          <Checkbox
                            inline
                            checked={checked}
                            indeterminate={indeterminate}
                            onChange={handleCheckAll}
                          />
                        </div>
                      </CustomHeaderCell>
                      <CheckCell
                        dataKey="id"
                        checkedKeys={checkedKeys}
                        onChange={handleCheck}
                      />
                    </Column>
                    <Column width={80}>
                      <CustomHeaderCell>
                        {t("REC")} {t("NO")}
                      </CustomHeaderCell>
                      <CustomCell dataKey="row_no" />
                    </Column>
                    <Column width={80}>
                      <CustomHeaderCell>{t("CODE")}</CustomHeaderCell>
                      <EditCell
                        rowData={(rowData: any) => rowData}
                        dataKey="country_code"
                        onChange={handleCellChange}
                      />
                    </Column>
                    <Column width={150}>
                      <CustomHeaderCell>{t("MOBILE")}</CustomHeaderCell>
                      <EditCell
                        rowData={(rowData: any) => rowData}
                        dataKey="mobile"
                        onChange={handleCellChange}
                      />
                    </Column>

                    <Column width={180}>
                      <CustomHeaderCell>
                        {t("FIRST")} {t("NAME")}
                      </CustomHeaderCell>
                      <EditCell
                        rowData={(rowData: any) => rowData}
                        dataKey="fname"
                        onChange={handleCellChange}
                      />
                    </Column>

                    <Column width={180}>
                      <CustomHeaderCell>
                        {t("LAST")} {t("NAME")}
                      </CustomHeaderCell>
                      <EditCell
                        rowData={(rowData: any) => rowData}
                        dataKey="lname"
                        onChange={handleCellChange}
                      />
                    </Column>
                    <Column width={130}>
                      <CustomHeaderCell>{t("GENDER")}</CustomHeaderCell>
                      <SelectEditCell
                        rowData={(rowData: any) => rowData}
                        dataKey="gender"
                        onChange={handleCellChange}
                        name="gender"
                        optionList={genderObjArr}
                      />
                    </Column>
                    <Column width={200} flexGrow={1}>
                      <CustomHeaderCell>
                        {t("HOUSE_NO")}/ {t("ADDRESS")}
                      </CustomHeaderCell>
                      <EditCell
                        rowData={(rowData: any) => rowData}
                        dataKey="house_number"
                        onChange={handleCellChange}
                      />
                    </Column>
                  </Table>
                </div>
                <Footer className="footer">
                  <Pagination
                    ellipsis
                    boundaryLinks
                    maxButtons={5}
                    size="xs"
                    layout={["total"]}
                    total={dataToImport.length}
                    limit={dataToImport.length}
                  />
                </Footer>
                <ToastContainer />
              </div>
            )}
          </div>
        </Content>
      </Container>
    </div>
  );
}

export default CustomerImport;
